// Conversation history management and compression
// Stores full history, compresses for API calls to fit token budget

const MAX_VERBATIM_MESSAGES = 6   // last N messages sent as-is
const MAX_HISTORY_TOKENS = 1500   // approximate token budget for history
const CHARS_PER_TOKEN = 4         // rough estimate for tokenization

let _messages = []

/**
 * Add a message to the history.
 * @param {string} role - 'user' | 'assistant' | 'action'
 * @param {string} content - message text
 * @param {object} meta - optional { model, classification, actions }
 */
export function addMessage(role, content, meta = {}) {
    _messages.push({
        role,
        content,
        timestamp: new Date().toISOString(),
        ...meta,
    })
}

/**
 * Add an action record to the history.
 * @param {string} description - e.g. "Navigated to Dashboard", "Filtered to Bagger, LKW"
 */
export function addAction(description) {
    addMessage('action', description)
}

/**
 * Get the full uncompressed history.
 */
export function getMessages() {
    return [..._messages]
}

/**
 * Get the number of user messages in the history.
 */
export function getUserMessageCount() {
    return _messages.filter(m => m.role === 'user').length
}

/**
 * Build compressed history for API calls.
 * Returns an array of {role: 'user'|'assistant', content: string} messages.
 */
export function buildApiHistory(language) {
    const lang = language === 'de' ? 'de' : 'en'

    // Filter to conversational messages (user + assistant + action)
    const relevant = _messages.filter(m =>
        m.role === 'user' || m.role === 'assistant' || m.role === 'action'
    )

    if (relevant.length === 0) return []

    // Split into recent (verbatim) and older (to compress)
    const recentStart = Math.max(0, relevant.length - MAX_VERBATIM_MESSAGES)
    const older = relevant.slice(0, recentStart)
    const recent = relevant.slice(recentStart)

    const result = []

    // Compress older messages into a summary
    if (older.length > 0) {
        const summary = _compressMessages(older, lang)
        if (summary) {
            result.push({ role: 'user', content: summary })
            result.push({ role: 'assistant', content: lang === 'de' ? 'Verstanden.' : 'Understood.' })
        }
    }

    // Add recent messages verbatim
    for (const msg of recent) {
        if (msg.role === 'action') {
            // Inline action records into the assistant flow
            const lastResult = result[result.length - 1]
            if (lastResult?.role === 'assistant') {
                lastResult.content += '\n[' + msg.content + ']'
            } else {
                result.push({ role: 'assistant', content: '[' + msg.content + ']' })
            }
        } else {
            result.push({ role: msg.role, content: msg.content })
        }
    }

    // Trim if over token budget
    return _trimToTokenBudget(result)
}

/**
 * Clear all history (conversation reset).
 */
export function clear() {
    _messages = []
}

// ─── Internal helpers ───────────────────────────────────────────────────────

function _compressMessages(messages, lang) {
    const summaryParts = []

    for (const msg of messages) {
        if (msg.role === 'user') {
            // Shorten user messages to first 80 chars
            const short = msg.content.length > 80
                ? msg.content.slice(0, 77) + '...'
                : msg.content
            summaryParts.push((lang === 'de' ? 'User: ' : 'User: ') + short)
        } else if (msg.role === 'assistant') {
            // Shorten assistant messages to first 80 chars
            const short = msg.content.length > 80
                ? msg.content.slice(0, 77) + '...'
                : msg.content
            summaryParts.push((lang === 'de' ? 'Bot: ' : 'Bot: ') + short)
        } else if (msg.role === 'action') {
            summaryParts.push('[' + msg.content + ']')
        }
    }

    const header = lang === 'de'
        ? 'Bisheriger Gesprächsverlauf:'
        : 'Previous conversation:'

    return header + '\n' + summaryParts.join('\n')
}

function _trimToTokenBudget(messages) {
    const totalChars = messages.reduce((s, m) => s + m.content.length, 0)
    const estimatedTokens = totalChars / CHARS_PER_TOKEN

    if (estimatedTokens <= MAX_HISTORY_TOKENS) return messages

    // Remove oldest messages until within budget
    while (messages.length > 2) {
        const totalChars = messages.reduce((s, m) => s + m.content.length, 0)
        if (totalChars / CHARS_PER_TOKEN <= MAX_HISTORY_TOKENS) break
        messages.shift()
    }

    return messages
}
